import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ProtectedEmail, ProtectedPhone } from '@/components/ProtectedContact';
import PageSEO from '@/components/PageSEO';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Clock, 
  Send,
  Loader2,
  CheckCircle,
  Facebook
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formSchema = z.object({
  nome: z.string().min(2, 'Nome richiesto'),
  email: z.string().email('Email non valida'),
  telefono: z.string().optional(),
  messaggio: z.string().min(10, 'Messaggio troppo breve'),
  privacy: z.boolean().refine(val => val === true, 'Devi accettare la privacy policy')
});

const ContattiPage = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { t } = useTranslation();

  const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm({
    resolver: zodResolver(formSchema)
  });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, data);
      setSuccess(true);
      toast.success('Messaggio inviato con successo!');
      reset();
      setTimeout(() => setSuccess(false), 5000);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore durante l\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Indirizzo',
      content: 'Piazza Pellegrino 8\n33038 San Daniele del Friuli (UD)',
      link: 'https://maps.google.com/?q=Piazza+Pellegrino+8+San+Daniele+del+Friuli'
    },
    {
      icon: Phone,
      title: 'Telefono',
      content: 'phone',
      link: null
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'email',
      link: null
    },
    {
      icon: Clock,
      title: 'Orari',
      content: 'Lun-Ven: 9:00 - 18:00\nSab-Dom: Chiuso',
      link: null
    }
  ];

  return (
    <div className="min-h-screen" data-testid="contatti-page">
      <PageSEO path="/contatti" />
      {/* Hero */}
      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="text-sm font-medium tracking-widest uppercase text-indigo-600 mb-4 block">
              {t('contatti.badge')}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight">
              {t('contatti.title')}
            </h1>
            <p className="text-lg text-slate-600">
              {t('contatti.subtitle')}
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-2"
            >
              <div className="bg-white rounded-2xl border border-slate-200 p-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">
                  Invia un messaggio
                </h2>

                {success && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-green-700">Messaggio inviato con successo!</span>
                  </motion.div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="nome">Nome *</Label>
                      <Input
                        id="nome"
                        {...register('nome')}
                        placeholder="Il tuo nome"
                        className="mt-2"
                        data-testid="contact-nome"
                      />
                      {errors.nome && (
                        <p className="text-red-500 text-sm mt-1">{errors.nome.message}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        {...register('email')}
                        placeholder="email@esempio.com"
                        className="mt-2"
                        data-testid="contact-email"
                      />
                      {errors.email && (
                        <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="telefono">Telefono (opzionale)</Label>
                    <Input
                      id="telefono"
                      {...register('telefono')}
                      placeholder="+39 333 1234567"
                      className="mt-2"
                      data-testid="contact-telefono"
                    />
                  </div>

                  <div>
                    <Label htmlFor="messaggio">Messaggio *</Label>
                    <Textarea
                      id="messaggio"
                      {...register('messaggio')}
                      placeholder="Come possiamo aiutarti?"
                      className="mt-2 min-h-[150px]"
                      data-testid="contact-messaggio"
                    />
                    {errors.messaggio && (
                      <p className="text-red-500 text-sm mt-1">{errors.messaggio.message}</p>
                    )}
                  </div>

                  {/* Privacy Consent */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Checkbox 
                        id="privacy"
                        checked={watch('privacy')}
                        onCheckedChange={(checked) => setValue('privacy', checked)}
                        data-testid="contact-privacy-checkbox"
                      />
                      <div className="text-sm">
                        <Label htmlFor="privacy" className="font-normal text-slate-600 cursor-pointer">
                          Ho letto e accetto la{' '}
                          <Link to="/privacy-policy" className="text-indigo-600 hover:underline" target="_blank">
                            Privacy Policy
                          </Link>{' '}
                          e acconsento al trattamento dei miei dati personali. *
                        </Label>
                        {errors.privacy && (
                          <p className="text-red-500 text-sm mt-1">{errors.privacy.message}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full btn-accent"
                    disabled={loading}
                    data-testid="contact-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Invio in corso...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Invia Messaggio
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="space-y-6"
            >
              {contactInfo.map((info, index) => (
                <div
                  key={info.title}
                  className="bg-slate-50 rounded-xl p-6 border border-slate-200"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">{info.title}</h3>
                      {info.content === 'phone' ? (
                        <ProtectedPhone parts={['+39', '0427', '40326']} display="+39 0427 40326" className="text-slate-600 hover:text-indigo-600 transition-colors" />
                      ) : info.content === 'email' ? (
                        <ProtectedEmail user="info" domain="studioazzanostp.it" className="text-slate-600 hover:text-indigo-600 transition-colors" />
                      ) : info.link ? (
                        <a 
                          href={info.link}
                          className="text-slate-600 hover:text-indigo-600 transition-colors whitespace-pre-line"
                          target={info.link.startsWith('http') ? '_blank' : undefined}
                          rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                        >
                          {info.content}
                        </a>
                      ) : (
                        <p className="text-slate-600 whitespace-pre-line">{info.content}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Facebook Link */}
              <a 
                href="https://www.facebook.com/studioazzanocantarutti/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-indigo-600 rounded-xl p-6 flex items-center gap-4 hover:bg-indigo-700 transition-colors"
                data-testid="facebook-contact-link"
              >
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Facebook className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">Seguici su Facebook</h3>
                  <p className="text-indigo-200 text-sm">Studio Professionale Azzano Cantarutti SRL</p>
                </div>
              </a>

              {/* Map Placeholder */}
              <div className="bg-slate-200 rounded-xl h-48 flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-slate-500 text-sm">Mappa interattiva</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Domande frequenti
            </h2>
            <p className="text-slate-600">
              Trova risposte rapide alle domande più comuni
            </p>
          </motion.div>

          <div className="space-y-4">
            {[
              {
                q: 'Come posso prenotare una consulenza?',
                a: 'Puoi prenotare una consulenza online dalla pagina dedicata, scegliendo data e orario disponibili.'
              },
              {
                q: 'Quali sono i tempi di risposta?',
                a: 'Rispondiamo a tutte le richieste entro 24 ore lavorative.'
              },
              {
                q: 'Offrite consulenze da remoto?',
                a: 'Sì, offriamo consulenze online tramite videochiamata per clienti in tutta Italia e all\'estero.'
              },
              {
                q: 'Come funziona il primo incontro?',
                a: 'Il primo incontro è dedicato a conoscere le tue esigenze e valutare insieme le soluzioni migliori per il tuo business.'
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * index }}
                className="bg-white rounded-xl p-6 border border-slate-200"
              >
                <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-slate-600 text-sm">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContattiPage;
